import type { CSSProperties, ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

interface ContainerProps extends Omit<ComponentPropsWithRef<"div">, "style"> {
  children: ReactNode;
  styles?: Readonly<Record<string, StyleValue>>;
}

const getContainerClass = (containerValue: string) => {
  switch (containerValue) {
    case "640px":
      return "wb-container wb-max-w-screen-sm";
    case "768px":
      return "wb-container wb-max-w-screen-md";
    case "1024px":
      return "wb-container wb-max-w-screen-lg";
    case "1280px":
      return "wb-container wb-max-w-screen-xl";
    case "1536px":
      return "wb-container wb-max-w-screen-2xl";
    case "none":
      return "wb-w-full";
    default:
      return "wb-container";
  }
};

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ children, styles = {}, className, ...rest }, ref) => {
    const container =
      String(styles.container?.value) || defaultStyles.container;

    const inlineStyles: CSSProperties = {
      ...parseStyles({
        styles,
        defaultStyles,
      }),
    };

    return (
      <div
        ref={ref}
        className={classNames(
          getContainerClass(container),
          "wb-mx-auto wb-p-4 wb-h-auto wb-min-h-10",
          className,
        )}
        style={inlineStyles}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Container.displayName = "Container";
